/**
 * Thin fetch wrappers for the Linkora signaling REST API. All paths are relative — same-origin
 * via the Caddy reverse proxy. Errors are normalised into a single shape so React components
 * don't need to branch on `res.ok`.
 *
 * Surface stays small on purpose: each method maps 1-to-1 to a server endpoint and returns the
 * canonical response shape. Composition (e.g. "claim then refresh list") lives in the calling
 * component, not here.
 */

// ----------------------------------------------------------------------------
// Response types — kept in sync with `signaling/src/db/users.ts` and `devices.ts`
// ----------------------------------------------------------------------------

export interface ApiUser {
  id: string;
  email: string;
  display_name: string | null;
  created_at: string;
  last_login_at: string | null;
  disabled: boolean;
  /** True when the account has completed the TOTP enrolment ceremony. */
  totp_enabled: boolean;
  totp_enabled_at: string | null;
}

export interface ApiAuthResponse {
  token: string;
  user: ApiUser;
}

export interface ApiDevice {
  id: string;
  user_id: string;
  host_id: string;
  nickname: string | null;
  added_at: string;
  last_connected: string | null;
  hostname: string | null;
  platform: 'windows' | 'macos' | 'linux' | null;
  agent_version: string | null;
  last_seen_at: string;
  /** Runtime: TRUE when the host currently has a live signaling socket. */
  online: boolean;
}

export interface TotpSetupResponse {
  /** Base32 secret. Shown to users who can't scan the QR (manual entry path). */
  secret: string;
  /** `otpauth://` URL — render to a QR code with any client-side QR library. */
  provisioning_uri: string;
}

export interface TotpEnableResponse {
  enabled: true;
  /**
   * One-time recovery codes — formatted `XXXXX-XXXXX`. Surface ONCE; the user is responsible
   * for saving them. The server stores only argon2id hashes from now on.
   */
  recovery_codes: string[];
}

// ----------------------------------------------------------------------------
// Error envelope
// ----------------------------------------------------------------------------

/**
 * Every non-2xx response from the API is wrapped in `ApiError`. Server-side errors arrive as
 * `{ error: "<code>" }`; we surface that string as both `.code` and `.message` so consumers
 * can `instanceof`-check + switch on a stable identifier.
 *
 * Codes seen in the wild:
 *   * `invalid_credentials` — wrong email or password (login).
 *   * `totp_required`       — login refused, account has 2FA enabled, code missing.
 *   * `totp_wrong`          — TOTP / recovery code didn't match.
 *   * `invalid_body`        — Zod validation failed (signup password policy etc.).
 *   * `rate_limited`        — bucket exhausted; reply carries `retryAfterSeconds`.
 *   * `email_already_used`  — signup with a taken email.
 *   * `host_not_found` / `host_disabled` / `host_password_expired` — claim flow rejects.
 *   * `bad_password`        — claim flow: wrong ephemeral password.
 *   * `not_found`           — DELETE/PATCH on a device not owned by caller.
 *   * `no_token` / `invalid_token` / `user_not_found_or_disabled` — auth middleware.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /** Optional server-supplied retry hint (in seconds) for 429 responses. */
    public readonly retryAfterSeconds?: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}, token?: string | null): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);
  const res = await fetch(path, { ...init, headers });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const code = (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string')
      ? body.error
      : `http_${res.status}`;
    const retry = (body && typeof body === 'object' && 'retryAfterSeconds' in body && typeof body.retryAfterSeconds === 'number')
      ? body.retryAfterSeconds
      : undefined;
    throw new ApiError(res.status, code, code, retry);
  }
  return body as T;
}

// ----------------------------------------------------------------------------
// API methods
// ----------------------------------------------------------------------------

/**
 * Login request body. `totp_code` / `recovery_code` are optional — only required when the user
 * has 2FA enabled. If neither is supplied and the server expects one, it returns 401 with
 * `error: "totp_required"` — the caller catches that and prompts for the code in a second step.
 */
export interface LoginRequest {
  email: string;
  password: string;
  totp_code?: string;
  recovery_code?: string;
}

export const api = {
  signup: (email: string, password: string, displayName?: string): Promise<ApiAuthResponse> =>
    request('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, display_name: displayName }),
    }),

  login: (req: LoginRequest): Promise<ApiAuthResponse> =>
    request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  me: (token: string): Promise<{ user: ApiUser }> => request('/api/auth/me', {}, token),

  // -- 2FA -------------------------------------------------------------------

  totpSetup: (token: string): Promise<TotpSetupResponse> =>
    request('/api/auth/2fa/setup', { method: 'POST' }, token),

  totpEnable: (token: string, totpCode: string): Promise<TotpEnableResponse> =>
    request(
      '/api/auth/2fa/enable',
      { method: 'POST', body: JSON.stringify({ totp_code: totpCode }) },
      token,
    ),

  totpDisable: (
    token: string,
    password: string,
    second: { totp_code?: string; recovery_code?: string },
  ): Promise<{ enabled: false }> =>
    request(
      '/api/auth/2fa/disable',
      { method: 'POST', body: JSON.stringify({ password, ...second }) },
      token,
    ),

  // -- Devices ---------------------------------------------------------------

  listDevices: (token: string): Promise<{ devices: ApiDevice[] }> =>
    request('/api/devices', {}, token),

  claimDevice: (token: string, hostId: string, password: string, nickname?: string): Promise<{ device: ApiDevice }> =>
    request(
      '/api/devices/claim',
      { method: 'POST', body: JSON.stringify({ host_id: hostId, password, nickname }) },
      token,
    ),

  renameDevice: (token: string, id: string, nickname: string | null): Promise<{ device: ApiDevice }> =>
    request(
      `/api/devices/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify({ nickname }) },
      token,
    ),

  removeDevice: (token: string, id: string): Promise<{ ok: true }> =>
    request(`/api/devices/${encodeURIComponent(id)}`, { method: 'DELETE' }, token),
};
